import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { filterUniqueAndMapToValueAndLabel } from 'App/data-export/store/selectors/_utils/filterUniqueAndMapToValueAndLabel'
import { getDataExportValueTypes } from 'App/data-export/store/selectors/_utils/getDataExportValueTypes'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import { translate } from 'i18n/i18n'
import type { TFieldAssetStoreState } from 'stores/fieldAssetStore/fieldAssetStore'
import { getIrrigationZoneEmitterExternalVendorNames } from 'utils/getIrrigationZoneEmitterExternalVendorNames'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'

export const fieldAssetsOptions = ({
  propertyId,
  fieldAssetStoreState,
}: {
  propertyId: string | null
  fieldAssetStoreState: TFieldAssetStoreState
}) => {
  const items =
    propertyId !== null
      ? getDataExportValueTypes({
          propertyId: Number(propertyId),
          fieldAssetStoreState,
        })
      : []

  const property = propertyId !== null ? fieldAssetStoreState.properties?.[Number(propertyId)] : undefined
  const pointsByLngLat = new Map(Object.entries(property?.points ?? {}))
  const irrigationZoneEmittersById = new Map(Object.entries(property?.irrigationZoneEmitters ?? {}))
  const pointsForProperty = Object.values(property?.points ?? {})
  const exportValueTypesByLngLat = new Map<string, string[]>()

  items.forEach((item) => {
    if (item.fieldAssetType === 'points') {
      exportValueTypesByLngLat.set(item.fieldAssetId, [
        ...(exportValueTypesByLngLat.get(item.fieldAssetId) ?? []),
        item.valueType,
      ])
    }
  })

  const pointsOptions = filterUniqueAndMapToValueAndLabel({
    items,
    filter: (x) => x.fieldAssetType === 'points',
    uniqueBy: (x) => x.fieldAssetId,
    mapToLabel: (x) => x.fieldAssetName,
    mapToExternalVendorNames: (x) =>
      getPointExternalVendorNames({
        point: pointsByLngLat.get(x.fieldAssetId),
        valueTypes: exportValueTypesByLngLat.get(x.fieldAssetId) ?? [],
      }),
  })

  const blocksOptions = filterUniqueAndMapToValueAndLabel({
    items,
    filter: (x) => x.fieldAssetType === 'blocks',
    uniqueBy: (x) => x.fieldAssetId,
    mapToLabel: (x) => x.fieldAssetName,
  })

  const irrigationZoneEmittersOptions = filterUniqueAndMapToValueAndLabel({
    items,
    filter: (x) => x.fieldAssetType === 'irrigation_zones',
    uniqueBy: (x) => x.fieldAssetId,
    mapToLabel: (x) => {
      const { emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(
        x.fieldAssetId as SharedTypes.IrrigationScheduler.TIrrigationZoneEmitterId,
      )

      return translate.phrases.templates('{{labelA}} ({{labelB}})', {
        labelA: x.fieldAssetName,
        labelB: mapApiEmitterTypeToLabel(emitterType),
      })
    },
    mapToExternalVendorNames: (x) => {
      const irrigationZoneEmitter = irrigationZoneEmittersById.get(x.fieldAssetId)

      return (
        irrigationZoneEmitter &&
        getIrrigationZoneEmitterExternalVendorNames({ irrigationZoneEmitter, points: pointsForProperty })
      )
    },
  })

  return {
    pointsOptions,
    blocksOptions,
    irrigationZoneEmittersOptions,
  }
}
